import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import { ReserveButton } from "@/components/layout/ReserveButton";
import { LineButton } from "@/components/layout/LineButton";
import { clinic } from "@/mocks/clinic";

type SlimZeroOption = {
  no: string;
  en: string;
  jp: string;
  text: string;
  href: string;
  cta: string;
};

/**
 * Medical Slim ZERO の4治療。
 * NOTE: 優劣・数値・効果などの未確認情報は含めない。
 *       ここでは「4つの選択肢がある」「詳細へ進める」ことのみを目的にする。
 */
const options: SlimZeroOption[] = [
  {
    no: "01",
    en: "INJECTION",
    jp: "新脂肪溶解注射",
    text: "気になる部位に対して行う医療痩身注射。",
    href: "/slim/injection",
    cta: "新脂肪溶解注射を見る",
  },
  {
    no: "02",
    en: "RF",
    jp: "RF（ラジオ波）",
    text: "ボディラインについて相談できるMedical Slim ZEROの治療方法の一つ。",
    href: "/slim/rf",
    cta: "RFを見る",
  },
  {
    no: "03",
    en: "FAT COOLING",
    jp: "脂肪冷却",
    text: "気になる部位について相談できる医療痩身治療の一つ。",
    href: "/slim/cooling",
    cta: "脂肪冷却を見る",
  },
  {
    no: "04",
    en: "MEDICAL EMS",
    jp: "医療用EMS",
    text: "ボディラインを医療の視点から考える治療選択肢の一つ。",
    href: "/slim/ems",
    cta: "医療用EMSを見る",
  },
];

/** Medical Slim ZERO親ページ用：4治療の比較・選択セクション＋ご相談導線 */
export default function SlimZeroOptions() {
  return (
    <>
      {/* TREATMENT OPTIONS */}
      <section id="tr-options" className="bg-background-50 py-16 md:py-20 lg:py-24">
        <div className="u-container">
          <Reveal>
            <div className="flex flex-col gap-5 md:max-w-2xl">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">TREATMENT OPTIONS</span>
              <span className="block h-px w-10 bg-accent-500" />
              <h2 className="font-heading text-[24px] leading-[1.55] tracking-[0.01em] text-foreground-950 md:text-[32px]">
                Medical Slim ZEROの4つの治療
              </h2>
              <p className="text-[13px] leading-[2.1] text-foreground-700 md:text-sm">
                Medical Slim ZEROでは、体型・気になる部位・お悩み・ご希望を確認しながら、4つの治療から一人ひとりに合わせて治療方法をご提案します。
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-12 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 lg:grid-cols-4 md:mt-16">
              {options.map((option) => (
                <Link
                  key={option.href}
                  to={option.href}
                  className="group flex flex-col bg-background-50 p-7 transition-colors hover:bg-secondary-100/50 md:p-8"
                >
                  <span className="font-label text-[12px] tracking-[0.25em] text-secondary-600">{option.no}</span>
                  <span className="en-eyebrow mt-7 block text-[10px] text-secondary-700 md:text-[11px]">
                    {option.en}
                  </span>
                  <h3 className="mt-3 font-heading text-[19px] text-foreground-950 md:text-xl">{option.jp}</h3>
                  <p className="mt-4 text-[12.5px] leading-[1.95] text-foreground-700">{option.text}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-9 text-[12.5px] text-foreground-800 transition-colors group-hover:text-accent-700">
                    {option.cta}
                    <i
                      className="ri-arrow-right-line text-[15px] transition-transform group-hover:translate-x-1"
                      aria-hidden
                    />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECONDARY CTA */}
      <section className="border-y border-background-300/60 bg-background-100 py-14 md:py-16">
        <div className="u-container">
          <Reveal>
            <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-12">
              <div className="md:max-w-xl">
                <h2 className="font-heading text-[21px] leading-[1.6] text-foreground-950 md:text-[26px]">
                  どの治療が自分に合っているか
                  <br className="hidden md:block" />
                  分からない方へ
                </h2>
                <p className="mt-4 text-[13px] leading-[2.1] text-foreground-700 md:text-sm">
                  体型や気になる部位、ご希望を伺い、一人ひとりに合わせた医療痩身をご提案します。
                </p>
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center md:shrink-0">
                <LineButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-7 py-3.5 text-[13.5px] font-medium text-background-50 transition-colors hover:bg-primary-700">
                  <i className="ri-line-fill text-base" aria-hidden />
                  LINEで相談
                </LineButton>
                <ReserveButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-950/25 bg-background-50 px-7 py-3.5 text-[13.5px] text-foreground-950 transition-colors hover:bg-background-50/70">
                  <i className="ri-calendar-check-line text-base" aria-hidden />
                  無料カウンセリングを予約
                </ReserveButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}